import { IModulesConfig } from 'interfaces/client';
import clientStore from 'store/client-store';
import socketStore from 'store/socket';
import vHistory, { VPathKey, VPathMethod } from './history';
import webSocket from './socket';
import { toJS } from 'mobx';
import moduleStore from 'store/module';
import { IModulePath, VModuleKey } from 'interfaces/module';
import bridge from './bridge';

const BTN_TEXT = {
  next: 'Next',
  startVideoCall: 'Start Video Call',
};

class Module {
  public getModulePathArr(): IModulePath {
    return moduleStore.getValue();
  }

  // should we use the config and default module to init a new module config,
  // then we don't have to check if it is enable
  public initModulePathArr(config: IModulesConfig | null | undefined) {
    console.log('start initModulePathArr');
    if (!config) return;
    for (let key in this.getModulePathArr()) {
      if (this.getModulePathArr().hasOwnProperty(key) && config.hasOwnProperty(key)) {
        switch (key) {
          case VModuleKey.pan:
            {
              // configurability error
              const panPhotoEnabled = config[key].panPhoto?.enabled;
              const panInfoEnabled = config[key].panInfo?.enabled;

              if (panPhotoEnabled === false && panInfoEnabled === false) {
                throw Error(
                  'We cannot set pan.panPhoto.enabled and pan.panInfo.enabled to false at the same time',
                );
              } else if (panPhotoEnabled === false) {
                this.getModulePathArr()[key as VModuleKey] = [VPathKey.PAN_INFO];
              } else if (panInfoEnabled === false) {
                this.getModulePathArr()[key as VModuleKey] = [VPathKey.PAN_CARD];
              }
              moduleStore.updateValue(this.getModulePathArr());
            }
            break;
          case VModuleKey.ktp:
            {
              // configurability error
              const ktpPhotoEnabled = config[key].ktpPhoto?.enabled;
              const ktpInfoEnabled = config[key].ktpInfo?.enabled;

              if (ktpPhotoEnabled === false && ktpInfoEnabled === false) {
                throw Error(
                  'We cannot set ktp.ktpPhoto.enabled and ktp.ktpInfo.enabled to false at the same time',
                );
              } else if (ktpPhotoEnabled === false) {
                this.getModulePathArr()[key as VModuleKey] = [VPathKey.KTP_INFO];
              } else if (ktpInfoEnabled === false) {
                this.getModulePathArr()[key as VModuleKey] = [VPathKey.KTP_CARD];
              }
              moduleStore.updateValue(this.getModulePathArr());
            }
            break;
          case VModuleKey.card:
            {
              // configurability error
              const cardUploadEnabled = config[key].upload?.enabled;
              const cardInfoEnabled = config[key].info?.enabled;

              if (cardUploadEnabled === false && cardInfoEnabled === false) {
                throw Error(
                  'We cannot set card.upload.enabled and pan.info.enabled to false at the same time',
                );
              } else if (cardUploadEnabled === false) {
                this.getModulePathArr()[key as VModuleKey] = [
                  VPathKey.CARD_SELECT,
                  VPathKey.INFO_SUBMIT,
                ];
              } else if (cardInfoEnabled === false) {
                this.getModulePathArr()[key as VModuleKey] = [
                  VPathKey.CARD_SELECT,
                  VPathKey.CARD_UPLOAD,
                ];
              }
              moduleStore.updateValue(this.getModulePathArr());
            }
            break;
          default:
            console.log(key, toJS(config[key]));
            break;
        }
      }
    }
    console.log('finish initModulePathArr', this.getModulePathArr());
  }

  public async checkEnvOnNextModule(module: VModuleKey, webCallback: () => void) {
    if (bridge.isWebView() && [VModuleKey.main, VModuleKey.precall].includes(module)) {
      if (module === VModuleKey.main) {
        await bridge.startVideoCall();
      } else if (module === VModuleKey.precall) {
        await bridge.startPrecall();
      }
      return true;
    } else {
      webCallback();
    }
  }

  public async initAndOpen() {
    try {
      const startModule = clientStore.getClientConfig().start || 'main';
      console.log('initAndOpen', startModule);
      // check env
      this.checkEnvOnNextModule(startModule as VModuleKey, () => {
        const currentStartPage = this.getFirstPageInModule(startModule);
        if (currentStartPage !== VPathKey.CONSENT) {
          socketStore.updateConsentAgreed(true);
        }
        if (currentStartPage === VPathKey.WAITING_ROOM) {
          webSocket.initAndOpen();
        }
        vHistory.replacePageByKey(currentStartPage);
      });
    } catch (e) {
      // if no path match
      alert(`no match path key ${clientStore.getClientConfig().start} ${e}`);
    }
  }

  public getCurrentModuleConfig() {
    const config = clientStore.getClientConfig().modules;
    try {
      const currentPathKey = vHistory.getKeyThroughPathname(location.pathname);
      const currentModuleKey = this.getModuleKeyOfPath(currentPathKey);
      return config ? config[currentModuleKey] || null : null;
    } catch (e) {
      return null;
    }
  }

  public getModuleConfig(currentModuleKey: VModuleKey) {
    const config = clientStore.getClientConfig().modules;
    return config ? config[currentModuleKey] || null : null;
  }

  // the last page of the module has to check the module config.
  // each time that we jump to the next module, we have to get the first page of the module

  public async nextPage(method: VPathMethod) {
    const config = clientStore.getClientConfig().modules;
    try {
      const currentPathKey = vHistory.getKeyThroughPathname(location.pathname);
      const currentModuleKey = this.getModuleKeyOfPath(currentPathKey);
      let nextPage = '';

      if (config?.hasOwnProperty(currentModuleKey)) {
        // get next page
        // check if it is the last enable page in the module
        // if true: check the module config to get the next module
        // if false: check the next page in current module
        const index = this.getModulePathArr()[currentModuleKey].indexOf(currentPathKey as VPathKey);
        // if next page is from new module
        if (index === this.getModulePathArr()[currentModuleKey].length - 1) {
          const nextModuleKey = config[currentModuleKey].next;
          const result = await this.checkEnvOnNextModule(nextModuleKey as VModuleKey, () => {
            if (nextModuleKey === VModuleKey.consent) {
              throw new Error('Consent page should be set to the first one');
            }
            if (nextModuleKey) {
              nextPage = this.getFirstPageInModule(nextModuleKey);
            } else {
              throw new Error('next module is null');
            }
          });
          if (result) {
            return;
          }
        } else {
          nextPage = this.getModulePathArr()[currentModuleKey][index + 1];
        }

        // connect the websocket and start video call, if the next page is waiting room
        if (nextPage === VPathKey.WAITING_ROOM) {
          webSocket.initAndOpen();
        }

        // jump to
        if (method === VPathMethod.REPLACE) {
          vHistory.replacePageByKey(nextPage);
        } else {
          vHistory.toPageByKey(nextPage);
        }
      } else {
        throw new Error('no such path, please check pageConfig');
      }
    } catch (e) {
      console.error(e);
      alert(e);
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    }
  }

  public getNextBtnText() {
    const config = clientStore.getClientConfig().modules;
    try {
      const currentPathKey = vHistory.getKeyThroughPathname(location.pathname);
      const currentModuleKey = this.getModuleKeyOfPath(currentPathKey);
      let nextBtnText = BTN_TEXT.next;

      if (config?.hasOwnProperty(currentModuleKey)) {
        const index = this.getModulePathArr()[currentModuleKey].indexOf(currentPathKey as VPathKey);
        if (index === this.getModulePathArr()[currentModuleKey].length - 1) {
          const nextModuleKey = config[currentModuleKey].next;
          if (nextModuleKey === VModuleKey.consent) {
            throw new Error('Consent page should be set to the first one');
          }
          if (
            nextModuleKey === VModuleKey.main ||
            nextModuleKey === VModuleKey.precall || // by default, precall is in front of main
            (nextModuleKey === VModuleKey.check &&
              config?.hasOwnProperty(nextModuleKey) &&
              (config[nextModuleKey].next === VModuleKey.main ||
                config[nextModuleKey].next === VModuleKey.precall))
          ) {
            nextBtnText = BTN_TEXT.startVideoCall;
          }
        }
      } else {
        throw new Error('no such path, please check pageConfig');
      }
      return nextBtnText;
    } catch (e) {
      console.error(e);
      alert(e);
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    }
  }

  public getFirstPageInModule(module: string): VPathKey {
    if (
      this.getModulePathArr().hasOwnProperty(module) &&
      this.getModulePathArr()[module as VModuleKey]?.length
    ) {
      return this.getModulePathArr()[module as VModuleKey][0];
    } else {
      throw new Error(`getFirstPageInModule, ${module}, no such page`);
    }
  }

  public getModuleKeyOfPath(currentPathKey: VPathKey | string): string {
    for (let key in this.getModulePathArr()) {
      if (this.getModulePathArr().hasOwnProperty(key)) {
        const result = this.getModulePathArr()[key as VModuleKey].includes(
          currentPathKey as VPathKey,
        );
        if (result) return key;
      }
    }
    throw new Error('getModuleKeyOfPath, no matching module key');
  }
}

const vModule = new Module();
export default vModule;

['development', 'staging'].includes(process.env.MODE!) && Object.assign(window, { vModule, toJS });
